import { Button, Icon } from "@screencloud/screencloud-ui-components";
import React, { ReactNode } from "react";
import {
  ActionsSlot,
  Body,
  CloseButtonSlot,
  Footer,
  FooterStartGroup,
  FooterStartSlot,
  Header,
  ProgressBar,
  ProgressBarFill,
  Title,
  Wrapper,
} from "./styles";

export interface WizardModalAction {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ["data-testid"]?: string;
}

export interface WizardModalDestructiveAction {
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  ["data-testid"]?: string;
}

export interface WizardModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  primaryAction?: WizardModalAction;
  secondaryAction?: WizardModalAction;
  destructiveAction?: WizardModalDestructiveAction;
  /** Optional content shown at the start (left) of the footer, e.g. a total. */
  footerStart?: ReactNode;
  progress?: number;
  width?: number | string;
  className?: string;
  ["data-testid"]?: string;
}

const clampProgress = (value: number): number => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

export const WizardModal: React.FC<WizardModalProps> = ({
  title,
  onClose,
  children,
  primaryAction,
  secondaryAction,
  destructiveAction,
  footerStart,
  progress,
  width,
  className,
  ...rest
}) => {
  const testId = rest["data-testid"];
  const tid = (suffix: string) =>
    testId ? `${testId}-${suffix}` : `wizard-modal-${suffix}`;
  const hasFooter = Boolean(
    primaryAction || secondaryAction || destructiveAction || footerStart,
  );

  return (
    <Wrapper $width={width} className={className} data-testid={testId}>
      <Header data-testid={tid("header")}>
        {progress !== undefined && (
          <ProgressBar data-testid={tid("progress")}>
            <ProgressBarFill
              $value={clampProgress(progress)}
              data-testid={tid("progress-fill")}
            />
          </ProgressBar>
        )}
        <Title>{title}</Title>
        <CloseButtonSlot>
          <Button
            transparent
            onClick={onClose}
            data-testid={tid("close")}
            title="Close"
          >
            <Icon name="close" />
          </Button>
        </CloseButtonSlot>
      </Header>

      <Body>{children}</Body>

      {hasFooter && (
        <Footer data-testid={tid("footer")}>
          <FooterStartGroup>
            {destructiveAction && (
              <Button
                transparent
                onClick={destructiveAction.onClick}
                disabled={destructiveAction.disabled}
                title={destructiveAction.tooltip}
                data-testid={
                  destructiveAction["data-testid"] ?? tid("destructive")
                }
              >
                <Icon name="trash" />
              </Button>
            )}
            {secondaryAction && (
              <Button
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
                data-testid={secondaryAction["data-testid"] ?? tid("secondary")}
              >
                {secondaryAction.label}
              </Button>
            )}
          </FooterStartGroup>
          <ActionsSlot>
            {footerStart && (
              <FooterStartSlot data-testid={tid("footer-start")}>
                {footerStart}
              </FooterStartSlot>
            )}
            {primaryAction && (
              <Button
                primary
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                data-testid={primaryAction["data-testid"] ?? tid("primary")}
              >
                {primaryAction.label}
              </Button>
            )}
          </ActionsSlot>
        </Footer>
      )}
    </Wrapper>
  );
};
